import type { FC, RefCallback } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { Box } from '@interstate/components/Box';
import type { TableColumn } from '@interstate/components/Table';
import { Table } from '@interstate/components/Table';
import type { IDetailedAuditLog, AuditLogDealInformation, IDealChangeRecord } from '@makemydeal/dr-platform-types';
import { PrinterIcon } from '@interstate/components/Icons';

import ChangeLogList from './ChangeLogList';
import DealInformation from './DealInformation';
import { transformation } from '../Transformation';
import { TableStyled } from './Table.style';
import PrintAuditLog from '../PrintPDF/PrintPDF.interstate';

const VersionList: FC<{
    versionData: AuditLogDealInformation[];
    changeLogData: IDetailedAuditLog[];
    lastItemRef: RefCallback<HTMLElement>;
    handleSetSlideoutProps: (selectedItem: IDealChangeRecord) => void;
}> = ({ versionData, changeLogData, lastItemRef, handleSetSlideoutProps }) => {
    const isEnablePrintAuditLog = useSelector(featureToggleSelectors.useEnablePrintAuditLog);

    // Group changeLogData by version for easier lookup
    const groupedChangeLogData = useMemo(() => {
        if (!changeLogData) return {};

        return changeLogData.reduce((acc: Record<string, IDetailedAuditLog>, item: IDetailedAuditLog) => {
            if (item && typeof item.version === 'string') {
                acc[item.version] = item;
            }
            return acc;
        }, {} as Record<string, IDetailedAuditLog>);
    }, [changeLogData]);

    // Function to render expanded changelog component
    const renderChangeLog = (record: AuditLogDealInformation) => {
        const version = record.version || '';
        const expandedRecord = groupedChangeLogData[version];

        if (!expandedRecord || !expandedRecord.changeLog || expandedRecord.changeLog.length === 0) {
            return <Box sx={{ display: 'flex', justifyContent: 'center' }}>No audit log available for this deal version.</Box>;
        }

        return (
            <ChangeLogList
                changeLogData={expandedRecord.changeLog.map((item: IDetailedAuditLog['changeLog'][number], idx: number) => ({
                    ...item,
                    key: idx
                }))}
                handleSetSlideoutProps={handleSetSlideoutProps}
            />
        );
    };

    const versionColumnDefs: TableColumn[] = [
        {
            dataIndex: 'createdOn',
            title: 'Date/Time',
            className: 'column-date-time',
            render: (value) => transformation.transformationDateTime(value)
        },
        {
            dataIndex: 'detail',
            title: 'Deal Information',
            render: (_, record) => {
                return <DealInformation data={record} />;
            },
            className: 'deal-information-column'
        },
        {
            dataIndex: 'applicationName',
            title: 'Source',
            className: 'column-source'
        },
        {
            dataIndex: 'dealerUser',
            title: 'Modified By',
            className: 'column-modifiedBy',
            render: (value) => transformation.transformationModifiedBy(value)
        }
    ];

    // Only show the Print Audit Log column if the feature toggle is enabled
    if (isEnablePrintAuditLog) {
        versionColumnDefs.push({
            dataIndex: 'printAuditLog',
            title: '',
            className: 'column-print-audit-log',
            render: (_, record) => {
                const changeLogRecord = groupedChangeLogData[record.version];

                // Only enable printing if there are actual changes to print in this version
                // (i.e., the changeLog array exists and has at least one record)
                if (Array.isArray(changeLogRecord?.changeLog) && changeLogRecord.changeLog.length > 0) {
                    // Show clickable printer icon that will generate a PDF for this version
                    return <PrintAuditLog version={record.version} />;
                }

                // Show disabled printer icon when there's nothing to print for this version
                return <PrinterIcon data-testid="disable-print-icon" fontSize={16} color="gray" cursor="default" />;
            }
        });
    }
    return (
        <Box>
            <TableStyled>
                <Table
                    rowClassName={() => 'deal-version'}
                    highlightOnHover={true}
                    data-testid="audit-log-table"
                    className="audit-log-table"
                    columns={versionColumnDefs}
                    data={versionData}
                    dataDensity="small"
                    bordered={true}
                    sortableColumns={false}
                    defaultPageSize={400}
                    expandedRowComponent={(record) => renderChangeLog(record)}
                    onRowCallback={(record, rowIndex) => {
                        const isLastRow = rowIndex === versionData.length - 1; // Check if it's the last row
                        return {
                            ref: isLastRow ? lastItemRef : undefined, // Attach the ref to the last row
                            style: {}
                        };
                    }}
                />
            </TableStyled>
        </Box>
    );
};

export default VersionList;
